// Sale Opportunities List Page
import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { saleOpportunityAPI, saleAPI } from '../../services/api';
import api from '../../services/api';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { QK } from '../../lib/queryKeys';
import { fetchCrmEntities, fetchHorses, saleOpportunityDetailQueryFn } from '../../lib/queryFunctions';
import { useAuth } from '../../context/AuthContext';
import { TableToolbar } from '../../components/common/TableToolbar';
import OnboardingBeacon from '../../components/onboarding/OnboardingBeacon';
import SidebarLayout from '../../components/layout/SidebarLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { toast } from 'sonner';
import { Plus, Search, ChevronRight, Gavel, Eye, Stethoscope, Trash2, Archive, CheckCircle2, Loader2, AlertCircle, CalendarClock } from 'lucide-react';
import { Checkbox } from '../../components/ui/checkbox';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { useConfirm } from '../../components/common/ConfirmDialog';

const stageBadges = {
  presale_create: null,
  presale_confirm: { label: 'Tracking Deliverables', className: 'bg-yellow-100 text-yellow-700' },
  presale_deliverables: { label: 'Tracking Deliverables', className: 'bg-yellow-100 text-yellow-700' },
  presale_ready: { label: 'Ready for Sale', className: 'bg-green-100 text-green-700' },
  live_sale: { label: 'Live Sale', className: 'bg-primary text-white' },
  post_sale: { label: 'Complete', className: 'bg-muted text-muted-foreground' },
};
const entryConfirmedBadge = { label: 'Entry Confirmed', className: 'bg-blue-100 text-blue-700' };
const enteredBadge = { label: 'Entered', className: 'bg-orange-100 text-orange-700' };
const notAcceptedBadge = { label: 'Not Accepted', className: 'bg-red-100 text-red-700' };
const withdrawnBadge = { label: 'Withdrawn', className: 'bg-red-100 text-red-700' };
const potentialSaleEntryBadge = { label: 'Potential Sale Entry', className: 'bg-gray-100 text-gray-700' };
const POTENTIAL_STATUSES = new Set(['not_yet_entered', 'nominated', 'still_under_consideration', 'accepted', '', null, undefined]);
const showPotentialSaleEntry = (s) => POTENTIAL_STATUSES.has(s);
const showEntered = (s) => s === 'entered';
const showNotAccepted = (s) => s === 'recommended_another_sale';
const showWithdrawn = (s) => s === 'withdrawn' || s === 'out';

const resultBadges = {
  sold: { label: 'SOLD', className: 'bg-green-600 text-white' },
  rna: { label: 'RNA', className: 'bg-yellow-600 text-white' },
  withdrawn: { label: 'Withdrawn', className: 'bg-red-100 text-red-700' },
};

// Mobile card row for a single opportunity
const OppMobileCard = ({ opp, sale, horse, stageBadge, firstSession, navigate }) => (
  <div
    className="flex items-center gap-3 p-3 border-b last:border-b-0 cursor-pointer hover:bg-muted/30 active:bg-muted/50"
    onClick={() => navigate(`/sales/opportunities/${opp.id}`)}
  >
    <div className="flex-1 min-w-0">
      <p className="text-sm font-semibold truncate">{opp.opportunity_name || '-'}</p>
      <p className="text-xs text-muted-foreground truncate">
        {opp.hip_number ? `Hip #${opp.hip_number}` : ''}
        {firstSession?.ship_in_date ? ` • Ship in: ${new Date(firstSession.ship_in_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : ''}
      </p>
    </div>
    <div className="flex items-center gap-2 shrink-0">
      {stageBadge && <Badge className={`text-xs ${stageBadge.className}`}>{stageBadge.label}</Badge>}
      {opp.result && opp.result !== 'pending' && (
        <Badge className={`text-xs ${opp.result === 'sold' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
          {opp.result.toUpperCase()}
        </Badge>
      )}
      <ChevronRight className="w-4 h-4 text-muted-foreground" />
    </div>
  </div>
);

const SaleOpportunitiesPage = () => {
  const { isViewOnly } = useAuth();
  const viewOnly = isViewOnly();
  const isImpersonating = !!localStorage.getItem('impersonation_id');
  const navigate = useNavigate();
  const confirm = useConfirm();
  const qc = useQueryClient();

  // ── UI state ──────────────────────────────────────────────────────────────
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState(null);
  const [selectedOppIds, setSelectedOppIds] = useState(new Set());
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [bulkConfirmDate, setBulkConfirmDate] = useState('');
  const [bulkConfirmStatuses, setBulkConfirmStatuses] = useState(null);
  const [bulkConfirmLoading, setBulkConfirmLoading] = useState(false);
  const [bulkConfirming, setBulkConfirming] = useState(false);
  // Bulk extension state (System Owner impersonating only)
  const [bulkExtOpen, setBulkExtOpen] = useState(false);
  const [bulkExtDeadline, setBulkExtDeadline] = useState('');
  const [bulkExtReason, setBulkExtReason] = useState('');
  const [bulkExtStatuses, setBulkExtStatuses] = useState(null);  // null=not loaded, {}=loaded
  const [bulkExtLoading, setBulkExtLoading] = useState(false);
  const [bulkExtending, setBulkExtending] = useState(false);
  const [viewHistoric, setViewHistoric] = useState(false);

  // ── Server state ──────────────────────────────────────────────────────────
  const { data: allOppsRaw = [], isLoading: loading } = useQuery({
    queryKey: QK.saleOpportunitiesAll(viewHistoric),
    queryFn: () => saleOpportunityAPI.list().then(r => r.data),
    onError: () => toast.error('Failed to load sale opportunities'),
  });
  const opportunities = useMemo(() =>
    viewHistoric ? allOppsRaw.filter(o => o.archived) : allOppsRaw.filter(o => !o.archived),
  [allOppsRaw, viewHistoric]);

  const { data: salesRaw = [] } = useQuery({
    queryKey: QK.allSales(),
    queryFn: () => saleAPI.list().then(r => r.data),
  });
  const { data: systemSales = [] } = useQuery({
    queryKey: QK.tenantSales(),
    queryFn: () => api.get('/sales-management/system-wide').then(r => r.data),
  });
  const sales = useMemo(() => {
    const all = [...salesRaw, ...systemSales];
    return Array.from(new Map(all.map(s => [s.id, s])).values());
  }, [salesRaw, systemSales]);

  // Shared caches — already populated from list pages
  const { data: allHorses = [] } = useQuery({
    queryKey: QK.horses({ include_archived: true }),
    queryFn: fetchHorses({ include_archived: true }),
  });
  const horses = useMemo(() => Object.fromEntries(allHorses.map(h => [h.id, h])), [allHorses]);

  const { data: allEntities = [] } = useQuery({
    queryKey: QK.crmEntities(),
    queryFn: fetchCrmEntities(),
  });

  // Sessions — per sale, parallelised
  const saleIds = useMemo(() => [...new Set(opportunities.map(o => o.sale_id).filter(Boolean))], [opportunities]);
  const { data: sessionsArr = [] } = useQuery({
    queryKey: QK.saleSessionsBatch([...saleIds].sort().join(',')),
    queryFn: () => Promise.all(saleIds.map(sid => api.get(`/sales/${sid}/sessions`).then(r => ({ id: sid, data: r.data || [] })))),
    enabled: saleIds.length > 0,
  });
  const sessions = useMemo(() => Object.fromEntries(sessionsArr.map(s => [s.id, s.data])), [sessionsArr]);

  // Owners — derived from allEntities + tenant fallback
  const owners = useMemo(() => {
    const entityMap = Object.fromEntries(allEntities.map(e => [e.id, e.system_name]));
    const map = {};
    opportunities.forEach(opp => {
      if (opp.owner_crm_entity_ids?.length > 0) {
        const names = opp.owner_crm_entity_ids.map(id => entityMap[id]).filter(Boolean);
        if (names.length > 0) map[opp.id] = names;
      }
    });
    return map;
  }, [opportunities, allEntities]);

  const invalidateOpps = () => {
    qc.invalidateQueries({ queryKey: QK.saleOpportunitiesAll(viewHistoric) });
    qc.invalidateQueries({ queryKey: QK.saleOpportunities() });
    // Invalidate historic list — a delete or stage change may affect archived entries
    qc.invalidateQueries({ queryKey: QK.historicSaleOpportunities() });
  };

  // Pre-warm the detail page data on row hover so navigation feels instant.
  // Uses a dedicated prefetch that does NOT override an already-fresh cache entry.
  const prefetchOpp = (oppId) => {
    qc.prefetchQuery({
      queryKey: QK.saleOpportunity(oppId),
      queryFn: saleOpportunityDetailQueryFn(oppId),
      staleTime: 30_000,
    });
  };
  
  const getOwnerNames = (opp) => {
    const ownerList = owners[opp.id];
    return Array.isArray(ownerList) && ownerList.length > 0 ? ownerList.join(', ') : '-';
  };

  const getSaleById = (id) => sales.find((s) => s.id === id);
  
  // Helper to get full sale name from opportunity
  const getFullSaleName = (opp) => {
    // Extract from opportunity name: "2026 Keeneland Horses of All Ages - Horse Name"
    if (opp.opportunity_name && opp.opportunity_name.includes(' - ')) {
      return opp.opportunity_name.split(' - ')[0];
    }
    // Fallback to sale name
    const sale = getSaleById(opp.sale_id);
    return sale?.sale_name || '-';
  };

  // ── Checkbox selection (computed after presaleOpps is defined below) ─────
  const toggleSelectAll = () => {
    setSelectedOppIds(prev => {
      const next = new Set(prev);
      if (allPresaleSelected) presaleOpps.forEach(o => next.delete(o.id));
      else                     presaleOpps.forEach(o => next.add(o.id));
      return next;
    });
  };
  const toggleSelectOpp = (id) => setSelectedOppIds(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  // ── Bulk confirm ──────────────────────────────────────────────────────────
  const openBulkConfirm = async () => {
    if (!selectedOppIds.size) return;
    setBulkConfirmDate(new Date().toISOString().split('T')[0]);
    setBulkConfirmStatuses(null);
    setBulkConfirmOpen(true);
    setBulkConfirmLoading(true);
    try {
      const results = {};
      await Promise.all([...selectedOppIds].map(async id => {
        const opp = presaleOpps.find(o => o.id === id);
        if (!opp) return;
        const sale = getSaleById(opp.sale_id);
        const isKeeneland = sale?.sale_company?.toLowerCase().includes('keeneland');

        if (opp.entry_status === 'confirmed') {
          results[id] = { opp, canConfirm: false, skipReason: 'Already confirmed' };
          return;
        }
        if (isKeeneland && !opp.property_line) {
          results[id] = { opp, canConfirm: false, skipReason: 'Keeneland sale: property line required' };
          return;
        }
        try {
          const { data } = await api.get(`/sale-opportunities/${id}/confirmation-status`);
          if (!data.allowed) {
            results[id] = { opp, canConfirm: false, skipReason: data.reason || 'Deadline has passed' };
          } else {
            results[id] = { opp, canConfirm: true };
          }
        } catch {
          results[id] = { opp, canConfirm: false, skipReason: 'Could not check status' };
        }
      }));
      setBulkConfirmStatuses(results);
    } finally {
      setBulkConfirmLoading(false);
    }
  };

  const submitBulkConfirm = async () => {
    if (!bulkConfirmDate) { toast.error('Date Confirmed is required'); return; }
    const toConfirm = Object.values(bulkConfirmStatuses || {}).filter(r => r.canConfirm);
    if (!toConfirm.length) return;
    setBulkConfirming(true);
    let confirmed = 0, failed = 0;
    for (const { opp } of toConfirm) {
      try {
        await saleOpportunityAPI.update(opp.id, {
          date_entered: bulkConfirmDate,
          entry_status: 'confirmed',
          property_line: opp.property_line || null,
        });
        confirmed++;
      } catch { failed++; }
    }
    setBulkConfirming(false);
    setBulkConfirmOpen(false);
    setSelectedOppIds(new Set());
    if (failed) toast.error(`${confirmed} confirmed, ${failed} failed`);
    else        toast.success(`${confirmed} ${confirmed === 1 ? 'entry' : 'entries'} confirmed`);
    invalidateOpps();
  };

  // ── Bulk Extension handlers (System Owner impersonating only) ───────────
  const openBulkExt = async () => {
    setBulkExtOpen(true);
    setBulkExtDeadline('');
    setBulkExtReason('');
    setBulkExtStatuses(null);
    setBulkExtLoading(true);
    const results = {};
    await Promise.all([...selectedOppIds].map(async id => {
      const opp = opportunities.find(o => o.id === id);
      if (!opp) return;
      try {
        const isConfirmed  = opp.entry_status === 'confirmed';
        const isWithdrawn  = opp.entry_status === 'withdrawn';
        const hasActiveExt = opp.extension_deadline &&
          new Date(opp.extension_deadline) > new Date();
        let canExtend = true;
        let skipReason = null;
        let warning = null;
        if (isConfirmed)       { canExtend = false; skipReason = 'Entry already confirmed'; }
        else if (isWithdrawn)  { canExtend = false; skipReason = 'Entry is withdrawn'; }
        else if (hasActiveExt) { warning = 'Will overwrite existing extension'; }
        results[id] = { opp, canExtend, skipReason, warning };
      } catch {
        results[id] = { opp, canExtend: false, skipReason: 'Could not check status' };
      }
    }));
    setBulkExtStatuses(results);
    setBulkExtLoading(false);
  };

  const submitBulkExt = async () => {
    if (!bulkExtDeadline) { toast.error('Extension deadline is required'); return; }
    if (!bulkExtReason.trim()) { toast.error('Reason is required'); return; }
    const toExtend = Object.values(bulkExtStatuses || {}).filter(r => r.canExtend);
    if (!toExtend.length) return;
    setBulkExtending(true);
    let granted = 0, failed = 0;
    const deadlineISO = new Date(bulkExtDeadline + 'T23:59:59').toISOString();
    for (const { opp } of toExtend) {
      try {
        await api.post(`/sale-opportunities/${opp.id}/extension`, {
          extension_deadline: deadlineISO,
          extension_reason:   bulkExtReason.trim(),
        });
        granted++;
      } catch { failed++; }
    }
    setBulkExtending(false);
    setBulkExtOpen(false);
    setSelectedOppIds(new Set());
    if (failed) toast.error(`${granted} extensions granted, ${failed} failed`);
    else        toast.success(`${granted} extension${granted === 1 ? '' : 's'} granted`);
    invalidateOpps();
  };
  // ── ─────────────────────────────────────────────────────────────────────

  const handleDeleteOpportunity = async (e, opportunityId) => {
    e.stopPropagation(); // Prevent row click navigation
    
    const confirmed = await confirm({ title: 'Are you sure you want to delete this sale opportunity? This action cannot be undone.', destructive: true });
    
    if (!confirmed) {
      return;
    }

    try {
      await saleOpportunityAPI.delete(opportunityId);
      toast.success('Sale opportunity deleted');
      invalidateOpps(); // Refresh the list
    } catch (error) {
      console.error('Failed to delete opportunity:', error);
      toast.error('Failed to delete opportunity');
    }
  };

  const matchesSearch = (opp) => {
    if (!searchTerm) return true;
    const q = searchTerm.toLowerCase();
    const sale = getSaleById(opp.sale_id);
    const horse = horses[opp.horse_id];
    const ownerList = owners[opp.id];
    const session = sessions[opp.sale_id]?.find(s => s.id === (opp.session_id || opp.sale_session_id));
    const sessionDate = session?.date ? new Date(session.date).toLocaleDateString() : '';
    const stageName = stageBadges[opp.stage]?.label || opp.stage || '';
    const resultName = resultBadges[opp.result]?.label || opp.result || '';
    const ownerStr = Array.isArray(ownerList) ? ownerList.join(', ') : '';
    return (
      opp.opportunity_name?.toLowerCase().includes(q) ||
      (sale?.sale_name || '').toLowerCase().includes(q) ||
      (horse?.system_name || '').toLowerCase().includes(q) ||
      ownerStr.toLowerCase().includes(q) ||
      stageName.toLowerCase().includes(q) ||
      String(opp.hip_number || '').toLowerCase().includes(q) ||
      sessionDate.toLowerCase().includes(q) ||
      resultName.toLowerCase().includes(q)
    );
  };

  const sortOpps = (arr) =>
    [...arr].sort((a, b) => {
      const saleA = (getSaleById(a.sale_id)?.sale_name || getFullSaleName(a)).toLowerCase();
      const saleB = (getSaleById(b.sale_id)?.sale_name || getFullSaleName(b)).toLowerCase();
      if (saleA !== saleB) return saleA.localeCompare(saleB);
      const horseA = (horses[a.horse_id]?.system_name || '').toLowerCase();
      const horseB = (horses[b.horse_id]?.system_name || '').toLowerCase();
      return horseA.localeCompare(horseB);
    });

  // Returns YYYY-MM-DD in the user's LOCAL timezone — safe for date-only comparisons
  const toLocalDate = (val) => {
    if (!val) return null;
    const d = new Date(val);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleDateString('sv'); // 'sv' locale always gives YYYY-MM-DD
  };

  const filterByStage = (stage) => {
    const todayStr = new Date().toLocaleDateString('sv'); // YYYY-MM-DD local

    const filtered = opportunities.filter((opp) => {
      if (!matchesSearch(opp)) return false;

      const sale = getSaleById(opp.sale_id);

      // --- Determine liveStart = earlier of (first ship-in date) or (sale start date) ---
      const saleStartStr = toLocalDate(sale?.start_date || sale?.sale_start_date);
      const saleEndStr   = toLocalDate(sale?.end_date   || sale?.sale_end_date);

      // Earliest ship-in date across the sessions for this sale
      const saleSessions = sessions[opp.sale_id] || [];
      let shipInStr = null;
      for (const s of saleSessions) {
        const d = toLocalDate(s.ship_in_date);
        if (d && (!shipInStr || d < shipInStr)) shipInStr = d;
      }

      // liveStart = earlier of shipIn and saleStart (whichever is set)
      let liveStartStr = null;
      if (shipInStr && saleStartStr) liveStartStr = shipInStr < saleStartStr ? shipInStr : saleStartStr;
      else liveStartStr = shipInStr || saleStartStr;

      // --- Post-sale: result entered OR today is after sale end date ---
      const hasResult  = opp.result && opp.result !== 'pending';
      const afterEnd   = saleEndStr && todayStr > saleEndStr;
      const isPost     = hasResult || afterEnd;

      // --- Live: liveStart has passed AND not yet post-sale ---
      const isLive = liveStartStr ? (todayStr >= liveStartStr && !isPost) : false;

      // --- Pre-sale: everything else ---
      const isPreSale = !isLive && !isPost;

      if (stage === 'presale') return isPreSale;
      if (stage === 'live')    return isLive;
      if (stage === 'post')    return isPost;
      return false;
    });
    return sortOpps(filtered);
  };

  const formatCurrency = (amount, currency = 'USD') => {
    if (!amount) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
    }).format(amount);
  };

  if (loading) {
    return (
      <SidebarLayout>
        <div className="space-y-6">
          <Skeleton className="h-10 w-48" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-96 w-full" />
        </div>
      </SidebarLayout>
    );
  }

  const presaleOpps = filterByStage('presale');
  const liveOpps = filterByStage('live');
  const postOpps = filterByStage('post');

  // ── Checkbox selection ────────────────────────────────────────────────────
  const allPresaleSelected = presaleOpps.length > 0 && presaleOpps.every(o => selectedOppIds.has(o.id));
  const somePresaleSelected = presaleOpps.some(o => selectedOppIds.has(o.id));

  const resolvedTab = activeTab || (liveOpps.length > 0 ? 'live' : 'presale');
  
  // For historic view, show all archived opportunities with expanded search + sorted
  const historicOpps = sortOpps(opportunities.filter(opp => matchesSearch(opp)));

  return (
    <SidebarLayout>
      <div className="space-y-6" data-testid="sale-opportunities-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="font-serif text-3xl font-semibold">
              {viewHistoric ? 'View Historic Sale Opportunities' : 'Sale Opportunities'}
            </h1>
            <p className="text-muted-foreground mt-1">
              {viewHistoric ? 'View archived sale opportunities' : 'Manage your horses at sale'}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            {!viewHistoric && !viewOnly && (
              <OnboardingBeacon itemKey="create_sale_entry">
                <Button
                  onClick={() => navigate('/sales/opportunities/new')}
                  className="btn-primary w-full sm:w-auto"
                  data-testid="new-opportunity-btn"
                >
                  <Plus className="w-4 h-4 mr-2" /> New Opportunity
                </Button>
              </OnboardingBeacon>
            )}
            <Button
              variant="outline"
              onClick={() => setViewHistoric(!viewHistoric)}
              className="w-full sm:w-auto"
              data-testid="historic-sales-btn"
            >
              {viewHistoric ? 'View Active' : 'View Historic'}
            </Button>
          </div>
        </div>

        {/* Search */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search opportunities..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 w-full"
                  data-testid="search-opportunities-input"
                />
              </div>
              <div className="hidden sm:flex justify-end">
              <TableToolbar
                inline
                tableId={`sale-opps-${resolvedTab}`}
                data={resolvedTab === 'presale' ? presaleOpps : resolvedTab === 'live' ? liveOpps : postOpps}
                filename={`sale-opportunities-${resolvedTab}`}
                columns={
                  resolvedTab === 'presale' ? [
                    { key: 'sale_name', label: 'Sale' },
                    { key: 'horse_name', label: 'Horse', accessor: r => r.horse?.system_name || r.opportunity_name },
                    { key: 'owners', label: 'Owner(s)', accessor: r => r.owner_names?.join(', ') || '-' },
                    { key: 'stage', label: 'Presale Stage' },
                    { key: 'ship_in_date', label: 'Ship In Date' },
                    { key: 'session_date', label: 'Session Date' },
                  ] : resolvedTab === 'live' ? [
                    { key: 'sale_name', label: 'Sale' },
                    { key: 'horse_name', label: 'Horse', accessor: r => r.horse?.system_name || r.opportunity_name },
                    { key: 'owners', label: 'Owner(s)', accessor: r => r.owner_names?.join(', ') || '-' },
                    { key: 'stage', label: 'Presale Stage' },
                    { key: 'hip_number', label: 'Hip #' },
                    { key: 'session_date', label: 'Session Date' },
                    { key: 'result', label: 'Result' },
                  ] : [
                    { key: 'sale_name', label: 'Sale' },
                    { key: 'horse_name', label: 'Horse', accessor: r => r.horse?.system_name || r.opportunity_name },
                    { key: 'owners', label: 'Owner(s)', accessor: r => r.owner_names?.join(', ') || '-' },
                    { key: 'stage', label: 'Presale Stage' },
                    { key: 'hip_number', label: 'Hip #' },
                    { key: 'result', label: 'Result' },
                  ]
                }
              />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs - Only show for active opportunities */}
        {!viewHistoric && (
          <Tabs value={resolvedTab} onValueChange={setActiveTab}>
            <div className="flex items-center justify-between gap-3">
              <div className="overflow-x-auto pb-0.5 flex-1">
                <TabsList className="whitespace-nowrap w-max sm:grid sm:w-full sm:grid-cols-3 sm:max-w-md">
                  <TabsTrigger value="presale" data-testid="tab-presale">
                    Pre-Sale ({presaleOpps.length})
                  </TabsTrigger>
                  <TabsTrigger value="live" data-testid="tab-live">
                    Live Sale ({liveOpps.length})
                  </TabsTrigger>
                  <TabsTrigger value="post" data-testid="tab-post">
                    Post-Sale ({postOpps.length})
                  </TabsTrigger>
                </TabsList>
              </div>
              {resolvedTab === 'presale' && !viewOnly && selectedOppIds.size > 0 && (
                <div className="flex items-center gap-2">
                  <Button
                    className="shrink-0 bg-primary hover:bg-primary/90 text-white gap-2"
                    onClick={openBulkConfirm}
                    data-testid="bulk-confirm-btn"
                  >
                    <CheckCircle2 className="w-4 h-4" />
                    Confirm Sale Entries ({selectedOppIds.size})
                  </Button>
                  {isImpersonating && (
                    <Button
                      variant="outline"
                      className="shrink-0 gap-2 border-primary text-primary hover:bg-primary/10"
                      onClick={openBulkExt}
                      data-testid="bulk-ext-btn"
                    >
                      <CalendarClock className="w-4 h-4" />
                      Grant Extensions ({selectedOppIds.size})
                    </Button>
                  )}
                </div>
              )}
            </div>

            {/* Pre-Sale Tab */}
            <TabsContent value="presale">
            <Card>
              <div className="overflow-x-auto">
              <Table className="table-heritage" data-table-id="sale-opps-presale">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[44px]" onClick={e => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={allPresaleSelected}
                        onChange={toggleSelectAll}
                        aria-label="Select all"
                        data-testid="select-all-checkbox"
                      />
                    </TableHead>
                    <TableHead>Sale</TableHead>
                    <TableHead>Horse</TableHead>
                    <TableHead>Owner(s)</TableHead>
                    <TableHead>Presale Stage</TableHead>
                    <TableHead>Ship In Date</TableHead>
                    <TableHead>Session Date</TableHead>
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {presaleOpps.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-12">
                        <Gavel className="w-12 h-12 mx-auto mb-3 text-muted-foreground/20" />
                        <p className="text-muted-foreground">No pre-sale opportunities</p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    presaleOpps.map((opp) => {
                      const sale = getSaleById(opp.sale_id);
                      const horse = horses[opp.horse_id];
                      const stageBadge = stageBadges[opp.stage];
                      const saleSessions = sessions[opp.sale_id] || [];
                      const firstSession = saleSessions[0];
                      const assignedSession = saleSessions.find(s => s.id === (opp.session_id || opp.sale_session_id)) || firstSession;

                      return (
                        <TableRow
                          key={opp.id}
                          className="cursor-pointer"
                          onClick={() => navigate(`/sales/opportunities/${opp.id}`)}
                          onMouseEnter={() => prefetchOpp(opp.id)}
                          data-testid={`opportunity-row-${opp.id}`}
                        >
                          <TableCell onClick={e => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={selectedOppIds.has(opp.id)}
                              onChange={() => toggleSelectOpp(opp.id)}
                              aria-label="Select entry"
                              data-testid={`select-opp-${opp.id}`}
                            />
                          </TableCell>

                          <TableCell>
                            <div>
                              <p className="text-sm font-medium">{getSaleById(opp.sale_id)?.sale_name || '-'}</p>
                              {(() => {
                                if (sale) {
                                  const year = new Date(sale.start_date || sale.sale_start_date).getFullYear();
                                  return (
                                    <p className="text-xs text-muted-foreground">
                                      {sale.sale_company} • {year}
                                    </p>
                                  );
                                }
                                return null;
                              })()}
                            </div>
                          </TableCell>
                          <TableCell>
                            <p className="font-medium">{horse?.system_name || '-'}</p>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm">{getOwnerNames(opp)}</span>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              <Badge className={stageBadge?.className}>
                                {stageBadge?.label}
                              </Badge>
                              {showPotentialSaleEntry(opp.entry_status) && <Badge className={`${potentialSaleEntryBadge.className} text-xs`}>{potentialSaleEntryBadge.label}</Badge>}
                              {showEntered(opp.entry_status) && <Badge className={`${enteredBadge.className} text-xs`}>{enteredBadge.label}</Badge>}
                              {showNotAccepted(opp.entry_status) && <Badge className={`${notAcceptedBadge.className} text-xs`}>{notAcceptedBadge.label}</Badge>}
                              {showWithdrawn(opp.entry_status) && <Badge className={`${withdrawnBadge.className} text-xs`}>{withdrawnBadge.label}</Badge>}
                              {opp.entry_status === 'confirmed' && (
                                <Badge className={`${entryConfirmedBadge.className} text-xs`}>{entryConfirmedBadge.label}</Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm">
                              {(opp.session_id || opp.sale_session_id)
                                ? (assignedSession?.ship_in_date
                                    ? new Date(assignedSession.ship_in_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                                    : '-')
                                : '-'}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm">
                              {assignedSession?.date 
                                ? new Date(assignedSession.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                                : '-'}
                            </span>
                          </TableCell>
                          <TableCell>
                            <ChevronRight className="w-4 h-4 text-muted-foreground" />
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
              </div>
            </Card>
          </TabsContent>

          {/* Live Sale Tab */}
          <TabsContent value="live">
            <Card>
              <div className="overflow-x-auto">
              <Table className="table-heritage" data-table-id="sale-opps-live">
                <TableHeader>
                  <TableRow>
                    <TableHead>Sale</TableHead>
                    <TableHead>Horse</TableHead>
                    <TableHead>Owner(s)</TableHead>
                    <TableHead>Presale Stage</TableHead>
                    <TableHead>Hip #</TableHead>
                    <TableHead>Session Date</TableHead>
                    <TableHead>Result</TableHead>
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {liveOpps.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-12">
                        <Gavel className="w-12 h-12 mx-auto mb-3 text-muted-foreground/20" />
                        <p className="text-muted-foreground">No horses at live sale</p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    liveOpps.map((opp) => {
                      const sale = getSaleById(opp.sale_id);
                      const horse = horses[opp.horse_id];
                      const saleSessions = sessions[opp.sale_id] || [];
                      const firstSession = saleSessions[0];
                      const assignedSession = saleSessions.find(s => s.id === (opp.session_id || opp.sale_session_id)) || firstSession;

                      return (
                        <TableRow
                          key={opp.id}
                          className="cursor-pointer"
                          onClick={() => navigate(`/sales/opportunities/${opp.id}`)}
                          onMouseEnter={() => prefetchOpp(opp.id)}
                          data-testid={`opportunity-row-${opp.id}`}
                        >
                          <TableCell>
                            <div>
                              <p className="text-sm font-medium">{sale?.sale_name || '-'}</p>
                              {sale && (
                                <p className="text-xs text-muted-foreground">
                                  {sale.sale_company} • {new Date(sale.start_date || sale.sale_start_date).getFullYear()}
                                </p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <p className="font-medium">{horse?.system_name || '-'}</p>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm">{getOwnerNames(opp)}</span>
                          </TableCell>
                          <TableCell>
                            {(() => {
                              // Map to presale stages only
                              const presaleStageMap = {
                                'presale_create': stageBadges.presale_create,
                                'presale_confirm': stageBadges.presale_confirm,
                                'presale_deliverables': stageBadges.presale_deliverables,
                                'presale_ready': stageBadges.presale_ready,
                                'live_sale': stageBadges.presale_ready, // Show Ready for Sale
                                'post_sale': stageBadges.presale_ready  // Show Ready for Sale
                              };
                              
                              const stageBadge = presaleStageMap[opp.stage] || null;
                              return (
                                <div className="flex flex-wrap gap-1">
                                  {stageBadge && <Badge className={stageBadge.className}>{stageBadge.label}</Badge>}
                                  {showPotentialSaleEntry(opp.entry_status) && <Badge className={`${potentialSaleEntryBadge.className} text-xs`}>{potentialSaleEntryBadge.label}</Badge>}
                                  {opp.entry_status === 'entered' && <Badge className={`${enteredBadge.className} text-xs`}>{enteredBadge.label}</Badge>}
                                  {opp.entry_status === 'confirmed' && (
                                    <Badge className={`${entryConfirmedBadge.className} text-xs`}>{entryConfirmedBadge.label}</Badge>
                                  )}
                                </div>
                              );
                            })()}
                          </TableCell>
                          <TableCell>
                            #{opp.hip_number || '-'}
                          </TableCell>
                          <TableCell>
                            <span className="text-sm">
                              {assignedSession?.date 
                                ? new Date(assignedSession.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                                : '-'}
                            </span>
                          </TableCell>
                          <TableCell>
                            {opp.result && opp.result !== 'pending' ? (
                              <Badge className={opp.result === 'sold' ? 'bg-green-100 text-green-700 text-xs' : opp.result === 'rna' ? 'bg-yellow-100 text-yellow-700 text-xs' : opp.result === 'withdrawn' ? 'bg-red-100 text-red-700 text-xs' : 'bg-gray-100 text-gray-700 text-xs'}>
                                {opp.result.toUpperCase()}
                              </Badge>
                            ) : <span className="text-sm text-muted-foreground">Pending</span>}
                          </TableCell>
                          <TableCell>
                            <ChevronRight className="w-4 h-4 text-muted-foreground" />
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
              </div>
            </Card>
          </TabsContent>

          {/* Post-Sale Tab */}
          <TabsContent value="post">
            <Card>
              <div className="overflow-x-auto">
              <Table className="table-heritage" data-table-id="sale-opps-post">
                <TableHeader>
                  <TableRow>
                    <TableHead>Sale</TableHead>
                    <TableHead>Horse</TableHead>
                    <TableHead>Owner(s)</TableHead>
                    <TableHead>Presale Stage</TableHead>
                    <TableHead>Hip #</TableHead>
                    <TableHead>Sale Result</TableHead>
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {postOpps.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-12">
                        <Gavel className="w-12 h-12 mx-auto mb-3 text-muted-foreground/20" />
                        <p className="text-muted-foreground">No completed sales</p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    postOpps.map((opp) => {
                      const sale = getSaleById(opp.sale_id);
                      const horse = horses[opp.horse_id];
                      const resultBadge = resultBadges[opp.result];

                      return (
                        <TableRow
                          key={opp.id}
                          className="cursor-pointer"
                          onClick={() => navigate(`/sales/opportunities/${opp.id}`)}
                          data-testid={`opportunity-row-${opp.id}`}
                        >
                          <TableCell>
                            <div>
                              <p className="text-sm font-medium">{getSaleById(opp.sale_id)?.sale_name || '-'}</p>
                              {(() => {
                                if (sale) {
                                  const year = new Date(sale.start_date || sale.sale_start_date).getFullYear();
                                  return (
                                    <p className="text-xs text-muted-foreground">
                                      {sale.sale_company} • {year}
                                    </p>
                                  );
                                }
                                return null;
                              })()}
                            </div>
                          </TableCell>
                          <TableCell>
                            <p className="font-medium">{horse?.system_name || '-'}</p>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm">{getOwnerNames(opp)}</span>
                          </TableCell>
                          <TableCell>
                            {(() => {
                              // Map to presale stages only
                              const presaleStageMap = {
                                'presale_create': stageBadges.presale_create,
                                'presale_confirm': stageBadges.presale_confirm,
                                'presale_deliverables': stageBadges.presale_deliverables,
                                'presale_ready': stageBadges.presale_ready,
                                'live_sale': stageBadges.presale_ready, // Show Ready for Sale
                                'post_sale': stageBadges.presale_ready  // Show Ready for Sale
                              };
                              
                              const stageBadge = presaleStageMap[opp.stage] || null;
                              return (
                                <div className="flex flex-wrap gap-1">
                                  {stageBadge && <Badge className={stageBadge.className}>{stageBadge.label}</Badge>}
                                  {showPotentialSaleEntry(opp.entry_status) && <Badge className={`${potentialSaleEntryBadge.className} text-xs`}>{potentialSaleEntryBadge.label}</Badge>}
                                  {opp.entry_status === 'entered' && <Badge className={`${enteredBadge.className} text-xs`}>{enteredBadge.label}</Badge>}
                                  {opp.entry_status === 'confirmed' && (
                                    <Badge className={`${entryConfirmedBadge.className} text-xs`}>{entryConfirmedBadge.label}</Badge>
                                  )}
                                </div>
                              );
                            })()}
                          </TableCell>
                          <TableCell>
                            #{opp.hip_number || '-'}
                          </TableCell>
                          <TableCell>
                            {resultBadge ? (
                              <Badge className={resultBadge.className}>{resultBadge.label}</Badge>
                            ) : (
                              <span className="text-sm">-</span>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
              </div>
            </Card>
          </TabsContent>
        </Tabs>
        )}

        {/* Bulk Confirm Dialog */}
        <Dialog open={bulkConfirmOpen} onOpenChange={open => { if (!bulkConfirming) setBulkConfirmOpen(open); }}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Confirm Sale Entries</DialogTitle>
              <DialogDescription>
                Confirm selected pre-sale entries in one step. Entries that cannot be confirmed are shown below.
              </DialogDescription>
            </DialogHeader>

            {/* Date picker */}
            <div className="space-y-2">
              <Label htmlFor="bulk-confirm-date" className="font-semibold">Date Confirmed <span className="text-red-500">*</span></Label>
              <input
                id="bulk-confirm-date"
                type="date"
                value={bulkConfirmDate}
                onChange={e => setBulkConfirmDate(e.target.value)}
                className="h-9 w-48 rounded-md border border-input px-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                data-testid="bulk-confirm-date-input"
              />
              <p className="text-xs text-muted-foreground">This date will be applied to all entries being confirmed.</p>
            </div>

            {/* Status table */}
            {bulkConfirmLoading ? (
              <div className="flex items-center gap-2 py-4 text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" /> Checking confirmation status…
              </div>
            ) : bulkConfirmStatuses ? (
              <div className="border rounded-md overflow-hidden text-sm">
                <table className="w-full">
                  <thead className="bg-muted/40">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium">Horse</th>
                      <th className="text-left px-3 py-2 font-medium">Sale</th>
                      <th className="text-left px-3 py-2 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.values(bulkConfirmStatuses).map(({ opp, canConfirm, skipReason }) => {
                      const horse = horses[opp.horse_id];
                      const sale  = getSaleById(opp.sale_id);
                      return (
                        <tr key={opp.id} className={`border-t ${canConfirm ? '' : 'opacity-60'}`}>
                          <td className="px-3 py-2">{horse?.system_name || '-'}</td>
                          <td className="px-3 py-2 text-muted-foreground">{sale?.sale_name || '-'}</td>
                          <td className="px-3 py-2">
                            {canConfirm ? (
                              <span className="flex items-center gap-1 text-green-600">
                                <CheckCircle2 className="w-3.5 h-3.5" />
                              </span>
                            ) : (
                              <span className="flex items-center gap-1 text-amber-600">
                                <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                                <span className="text-xs">{skipReason}</span>
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : null}

            {bulkConfirmStatuses && (() => {
              const toConfirm = Object.values(bulkConfirmStatuses).filter(r => r.canConfirm).length;
              const toSkip    = Object.values(bulkConfirmStatuses).filter(r => !r.canConfirm).length;
              return (
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground">
                    <strong>{toConfirm}</strong> {toConfirm === 1 ? 'entry' : 'entries'} will be confirmed
                    {toSkip > 0 && <>, <strong>{toSkip}</strong> will be skipped</>}.
                  </p>
                  {toConfirm > 0 && (
                    <>
                      <hr className="border-border" />
                      <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                        Confirming {toConfirm} {toConfirm === 1 ? 'entry' : 'entries'} will deduct <strong>{toConfirm} {toConfirm === 1 ? 'credit' : 'credits'}</strong> from your account.
                      </p>
                    </>
                  )}
                </div>
              );
            })()}

            <DialogFooter>
              <Button variant="outline" onClick={() => setBulkConfirmOpen(false)} disabled={bulkConfirming}>
                Cancel
              </Button>
              <Button
                className="bg-primary hover:bg-primary/90 text-white"
                onClick={submitBulkConfirm}
                disabled={
                  bulkConfirmLoading || bulkConfirming || !bulkConfirmDate ||
                  !Object.values(bulkConfirmStatuses || {}).some(r => r.canConfirm)
                }
                data-testid="bulk-confirm-submit-btn"
              >
                {bulkConfirming
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Confirming…</>
                  : `Confirm ${Object.values(bulkConfirmStatuses || {}).filter(r => r.canConfirm).length} ${Object.values(bulkConfirmStatuses || {}).filter(r => r.canConfirm).length === 1 ? 'Entry' : 'Entries'}`
                }
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Grant Extensions Dialog (System Owner impersonating only) */}
        <Dialog open={bulkExtOpen} onOpenChange={v => { if (!bulkExtending) setBulkExtOpen(v); }}>
          <DialogContent className="max-w-xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CalendarClock className="w-4 h-4 text-primary" />
                Bulk Grant Confirmation Extensions
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-2">
              {/* Deadline */}
              <div className="space-y-1.5">
                <Label>Extension Deadline *</Label>
                <input
                  type="date"
                  value={bulkExtDeadline}
                  onChange={e => setBulkExtDeadline(e.target.value)}
                  className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:ring-1 focus-visible:ring-ring"
                  min={new Date().toISOString().split('T')[0]}
                  data-testid="bulk-ext-deadline-input"
                />
              </div>

              {/* Reason */}
              <div className="space-y-1.5">
                <Label>Reason *</Label>
                <Textarea
                  value={bulkExtReason}
                  onChange={e => setBulkExtReason(e.target.value)}
                  placeholder="e.g. Client requested additional time to arrange payment"
                  rows={2}
                  data-testid="bulk-ext-reason-input"
                />
              </div>

              {/* Per-opportunity status table */}
              {bulkExtLoading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Checking eligibility…
                </div>
              ) : bulkExtStatuses ? (
                <div className="rounded-lg border overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left px-3 py-2 font-medium">Hip</th>
                        <th className="text-left px-3 py-2 font-medium">Horse</th>
                        <th className="text-left px-3 py-2 font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {Object.values(bulkExtStatuses).map(({ opp, canExtend, skipReason, warning }) => (
                        <tr key={opp.id} className="hover:bg-muted/20">
                          <td className="px-3 py-2 text-muted-foreground">{opp.hip_number || '—'}</td>
                          <td className="px-3 py-2 font-medium">{opp.horse_name || opp.horse_id}</td>
                          <td className="px-3 py-2">
                            {canExtend ? (
                              warning ? (
                                <span className="flex items-center gap-1 text-amber-600">
                                  <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                                  <span className="text-xs">{warning}</span>
                                </span>
                              ) : (
                                <span className="flex items-center gap-1 text-green-600">
                                  <span className="text-xs">Will grant extension</span>
                                  <CheckCircle2 className="w-3.5 h-3.5" />
                                </span>
                              )
                            ) : (
                              <span className="flex items-center gap-1 text-amber-600">
                                <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                                <span className="text-xs">{skipReason}</span>
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}

              {bulkExtStatuses && (() => {
                const eligible = Object.values(bulkExtStatuses).filter(r => r.canExtend).length;
                const skipped  = Object.values(bulkExtStatuses).filter(r => !r.canExtend).length;
                return (
                  <p className="text-sm text-muted-foreground">
                    <span className="font-medium text-foreground">{eligible}</span> extension{eligible !== 1 ? 's' : ''} will be granted
                    {skipped > 0 && `, ${skipped} skipped`}.
                  </p>
                );
              })()}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setBulkExtOpen(false)} disabled={bulkExtending}>
                Cancel
              </Button>
              <Button
                className="bg-primary hover:bg-primary/90 text-white"
                onClick={submitBulkExt}
                disabled={
                  bulkExtLoading || bulkExtending || !bulkExtDeadline || !bulkExtReason.trim() ||
                  !Object.values(bulkExtStatuses || {}).some(r => r.canExtend)
                }
                data-testid="bulk-ext-submit-btn"
              >
                {bulkExtending
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Granting…</>
                  : `Grant ${Object.values(bulkExtStatuses || {}).filter(r => r.canExtend).length} Extension${Object.values(bulkExtStatuses || {}).filter(r => r.canExtend).length !== 1 ? 's' : ''}`
                }
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Historic View - Single List (No Tabs) */}
        {viewHistoric && (
          <Card>
            <Table className="table-heritage">
              <TableHeader>
                <TableRow>
                  <TableHead>Sale</TableHead>
                  <TableHead>Horse</TableHead>
                  <TableHead>Owner(s)</TableHead>
                  <TableHead>Stage</TableHead>
                  <TableHead>Hip #</TableHead>
                  <TableHead>Result</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {historicOpps.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      No archived sale opportunities found
                    </TableCell>
                  </TableRow>
                ) : (
                  historicOpps.map(opp => {
                    const sale = sales.find(s => s.id === opp.sale_id);
                    const horse = horses[opp.horse_id];
                    
                    // Extract horse name from opportunity_name if horse not found
                    let horseName = horse?.system_name || horse?.name || 'Unknown';
                    if (!horse && opp.opportunity_name && opp.opportunity_name.includes(' - ')) {
                      horseName = opp.opportunity_name.split(' - ')[1] || 'Unknown';
                    }
                    
                    const ownerNames = owners[opp.id] || [];
                    const stageBadge = stageBadges[opp.stage] || stageBadges.presale_create;
                    const resultBadge = opp.result ? resultBadges[opp.result] : null;

                    return (
                      <TableRow
                        key={opp.id}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => navigate(`/sales/opportunities/${opp.id}`)}
                      >
                        <TableCell>
                          <div>
                            <p className="font-medium">{sale?.sale_name || 'Unknown Sale'}</p>
                            <p className="text-xs text-muted-foreground">
                              {sale?.sale_company || 'Unknown Company'}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <p className="font-medium">{horse?.system_name || horseName}</p>
                          {horse?.nickname && (
                            <p className="text-xs text-muted-foreground">"{horse.nickname}"</p>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            {Array.isArray(ownerNames) && ownerNames.length > 0 ? (
                              ownerNames.map((name, idx) => (
                                <p key={idx}>{name}</p>
                              ))
                            ) : (
                              <p className="text-muted-foreground">No owners</p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {stageBadge && <Badge className={stageBadge.className}>{stageBadge.label}</Badge>}
                            {showPotentialSaleEntry(opp.entry_status) && <Badge className={`${potentialSaleEntryBadge.className} text-xs`}>{potentialSaleEntryBadge.label}</Badge>}
                            {opp.entry_status === 'entered' && <Badge className={`${enteredBadge.className} text-xs`}>{enteredBadge.label}</Badge>}
                            {opp.entry_status === 'confirmed' && (
                              <Badge className={`${entryConfirmedBadge.className} text-xs`}>{entryConfirmedBadge.label}</Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm">
                            {opp.hip_number || '-'}
                          </span>
                        </TableCell>
                        <TableCell>
                          {resultBadge ? (
                            <Badge className={resultBadge.className}>
                              {resultBadge.label}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground text-sm">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <ChevronRight className="w-4 h-4 text-muted-foreground" />
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </Card>
        )}
      </div>
    </SidebarLayout>
  );
};

export default SaleOpportunitiesPage;
